import { CloseButton, Menu, Table } from '@mantine/core';
import {
	IconCalculator,
	IconChevronDown,
	IconChevronUp,
	IconCircle1,
	IconCircle2,
	IconCircle3,
	IconCircle4,
	IconCoinPound,
	IconTrash,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { NoDataFoundTableRow } from '../../../components/DataTable';
import { DeleteModal } from '../../../components/DeleteModal';
import { TableRowCollapsible } from '../../../components/TableRowCollapsible';
import { TableRowMenu } from '../../../components/TableRowMenu';
import { useModal } from '../../../hooks/useModal';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import { mutate } from '../../../util/api/mutate';
import { formatToCurrency, formatToThousands } from '../../../util/formatting';
import { undefinedCheck } from '../../../util/undefinedCheck';
import type { BranchStockDto } from '../types/BranchStockDto';
import { EditBranchStockModal } from './EditBranchStockModal';
import { EditBranchStockPriceModal } from './EditBranchStockPriceModal';
import { KeyedRow } from '../../../components/KeyedRow';

interface BranchStockTableRowProps {
	defaultBranchStock: BranchStockDto;
	refreshTable: () => void;
	skuid: string;
	vatRateId?: string;
}

export function BranchStockTableRow({
	defaultBranchStock,
	refreshTable,
	skuid,
	vatRateId,
}: BranchStockTableRowProps) {
	const { organisation } = useParams();

	const [branchStock, setBranchStock] = useState(defaultBranchStock);

	const [pricesOpen, setPricesOpen] = useState(false);

	const editModal = useModal();
	const editPriceModal = useModal();
	const deleteModal = useModal();

	const getPriceNumberIcon = (numberAsString: string) => {
		const number = Number.parseInt(numberAsString);

		switch (number) {
			case 1:
				return <IconCircle1 {...defaultIconStyle} />;
			case 2:
				return <IconCircle2 {...defaultIconStyle} />;
			case 3:
				return <IconCircle3 {...defaultIconStyle} />;
			case 4:
				return <IconCircle4 {...defaultIconStyle} />;
		}
	};

	const deleteBranchStockMutation = mutate<boolean>(
		`${organisation}/branch-stock/delete/${skuid}/${branchStock.branchId}`,
	);

	return (
		<>
			<EditBranchStockPriceModal
				close={editPriceModal.close}
				isOpened={editPriceModal.isOpen}
				updateProductRecord={(b) => setBranchStock(b)}
				encodedSkuid={branchStock.skuid}
				branchId={branchStock.branchId}
				vatRateId={vatRateId}
				defaultBranchStockPrice={{
					priceIncVatOne: branchStock.prices[0].priceIncVat,
					priceExcVatOne: branchStock.prices[0].priceExcVat,
					priceIncVatTwo: branchStock.prices[1].priceIncVat,
					priceExcVatTwo: branchStock.prices[1].priceExcVat,
					priceIncVatThree: branchStock.prices[2].priceIncVat,
					priceExcVatThree: branchStock.prices[2].priceExcVat,
					priceIncVatFour: branchStock.prices[3].priceIncVat,
					priceExcVatFour: branchStock.prices[3].priceExcVat,
				}}
			/>
			<EditBranchStockModal
				close={editModal.close}
				isOpened={editModal.isOpen}
				updateProductRecord={(b) => setBranchStock(b)}
				skuid={branchStock.skuid}
				branchName={branchStock.branchName}
				branchId={branchStock.branchId}
				defaultBranchStock={{
					branchIds: [],
					skuid: branchStock.skuid,
					description: branchStock.description,
					minStock: branchStock.minStock,
					maxStock: branchStock.maxStock,
					stock: branchStock.stock,
					isEdit: true,
				}}
			/>
			<DeleteModal
				close={deleteModal.close}
				isOpened={deleteModal.isOpen}
				titleType={'Branch Stock'}
				itemDescription={`Branch Stock: ${branchStock.branchName}`}
				mutation={deleteBranchStockMutation}
				onComplete={refreshTable}
			/>
			<KeyedRow>
				<Table.Td>{undefinedCheck(branchStock.branchName)}</Table.Td>
				<Table.Td>{undefinedCheck(branchStock.areaName)}</Table.Td>
				<Table.Td>{undefinedCheck(branchStock.description)}</Table.Td>
				<Table.Td>{formatToThousands(branchStock.stock)}</Table.Td>
				<Table.Td>{formatToThousands(branchStock.minStock)}</Table.Td>
				<Table.Td>{formatToThousands(branchStock.maxStock)}</Table.Td>
				<Table.Td>
					<CloseButton
						icon={
							!pricesOpen ? (
								<IconChevronDown {...defaultIconStyle} />
							) : (
								<IconChevronUp {...defaultIconStyle} />
							)
						}
						onClick={() => setPricesOpen(!pricesOpen)}
					/>
				</Table.Td>
				<Table.Td>
					<TableRowMenu>
						<Menu.Item
							leftSection={<IconCalculator {...defaultIconStyle} />}
							data-cy={'edit-button'}
							onClick={() => editModal.open()}
						>
							Edit Branch Stock
						</Menu.Item>
						<Menu.Item
							leftSection={<IconCoinPound {...defaultIconStyle} />}
							data-cy={'edit-button'}
							onClick={() => editPriceModal.open()}
						>
							Edit Prices
						</Menu.Item>
						<Menu.Item
							leftSection={<IconTrash {...defaultIconStyle} />}
							c={'red'}
							onClick={() => deleteModal.open()}
						>
							Delete
						</Menu.Item>
					</TableRowMenu>
				</Table.Td>
			</KeyedRow>
			<TableRowCollapsible isOpen={pricesOpen}>
				<Table>
					<Table.Thead>
						<KeyedRow>
							<Table.Th>Number</Table.Th>
							<Table.Th>Price Exc. VAT</Table.Th>
							<Table.Th>Price Inc. VAT</Table.Th>
							<Table.Th>VAT</Table.Th>
						</KeyedRow>
					</Table.Thead>
					<Table.Tbody>
						{branchStock.prices?.map((price) => (
							<>
								<KeyedRow key={price.number}>
									<Table.Td>{getPriceNumberIcon(price.number)}</Table.Td>
									<Table.Td>{formatToCurrency(price.priceExcVat)}</Table.Td>
									<Table.Td>{formatToCurrency(price.priceIncVat)}</Table.Td>
									<Table.Td>
										{price.priceIncVat !== undefined &&
										price.priceExcVat !== undefined
											? formatToCurrency(price.priceIncVat - price.priceExcVat)
											: formatToCurrency(0)}
									</Table.Td>
								</KeyedRow>
							</>
						))}
						{branchStock.prices && branchStock.prices?.length < 1 && (
							<NoDataFoundTableRow pt={0} />
						)}
					</Table.Tbody>
				</Table>
			</TableRowCollapsible>
		</>
	);
}
